import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { UserPlus, Trash2, Shield, Briefcase, Truck, KeyRound, Pencil, Lock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import PasswordInput from "../components/PasswordInput";
import { carregarPlanoAtual, abrirUpgradeModal, temFeatureSync } from "../lib/planos";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function VendedoresPage() {
  const [acessos, setAcessos] = useState([]);
  const [carros, setCarros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState("vendedor"); // "vendedor" ou "secretario"
  const [permissoesDialogOpen, setPermissoesDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const [formVendedor, setFormVendedor] = useState({
    nome: "", email: "", senha: "", carro_id: ""
  });
  const [formSecretario, setFormSecretario] = useState({
    nome: "", email: "", senha: ""
  });

  const defaultPermissoes = {
    criar_cliente: true,
    editar_cliente: true,
    excluir_cliente: false,
    criar_promissoria: true,
    editar_promissoria: true,
    quitar_promissoria: true,
    excluir_promissoria: false,
    compartilhar_pdf: true,
    acessar_dividas: true,
    acessar_fluxo_caixa: false
  };
  const [permissoes, setPermissoes] = useState(defaultPermissoes);

  // Edit credenciais
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const [credDialogOpen, setCredDialogOpen] = useState(false);
  const [credForm, setCredForm] = useState({ nome: "", email: "", senha: "", current_password: "" });
  const [credTarget, setCredTarget] = useState(null);
  const [credSaving, setCredSaving] = useState(false);

  useEffect(() => {
    fetchAcessos();
    fetchCarros();
    carregarPlanoAtual().catch(() => {});
  }, []);

  const fetchAcessos = async () => {
    try {
      const response = await axios.get(`${API}/acessos`);
      setAcessos(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Apenas administradores podem acessar esta página");
      } else {
        toast.error("Erro ao carregar acessos");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCarros = async () => {
    try {
      const response = await axios.get(`${API}/carros`);
      setCarros(response.data);
    } catch (error) {
      console.error("Erro ao carregar carros:", error);
    }
  };

  const handleSubmitVendedor = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/vendedores`, formVendedor);
      toast.success("Vendedor criado com sucesso!");
      setDialogOpen(false);
      setFormVendedor({ nome: "", email: "", senha: "", carro_id: "" });
      fetchAcessos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar vendedor");
    }
  };

  const handleSubmitSecretario = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/secretarios`, formSecretario);
      toast.success("Secretário criado com sucesso!");
      setDialogOpen(false);
      setFormSecretario({ nome: "", email: "", senha: "" });
      fetchAcessos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar secretário");
    }
  };

  const handleUpdatePermissoes = async () => {
    if (!selectedItem) return;
    try {
      await axios.put(`${API}/vendedores/${selectedItem.id}/permissoes`, permissoes);
      toast.success("Permissões atualizadas com sucesso!");
      setPermissoesDialogOpen(false);
      fetchAcessos();
    } catch (error) {
      toast.error("Erro ao atualizar permissões");
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      await axios.delete(`${API}/vendedores/${itemToDelete.id}`);
      toast.success(`${itemToDelete.tipo === 'secretario' ? 'Secretário' : 'Vendedor'} excluído com sucesso!`);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchAcessos();
    } catch (error) {
      toast.error("Erro ao excluir");
    }
  };

  const openPermissoesDialog = (item) => {
    setSelectedItem(item);
    setPermissoes({ ...defaultPermissoes, ...(item.permissoes || {}) });
    setPermissoesDialogOpen(true);
  };

  const openDeleteDialog = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const openCreateDialog = (type) => {
    setDialogType(type);
    setDialogOpen(true);
  };

  // ===== Editar credenciais =====
  const abrirCredDialog = (item) => {
    setCredTarget(item);
    setCredForm({ nome: item.nome || "", email: item.email || "", senha: "", current_password: "" });
    setCredDialogOpen(true);
  };

  const salvarCredenciais = async (e) => {
    e?.preventDefault?.();
    if (!credTarget) return;
    const isSelf = credTarget.id === currentUser.id;
    if (isSelf && !credForm.current_password) {
      toast.error("Informe sua senha atual para confirmar.");
      return;
    }
    setCredSaving(true);
    try {
      const payload = {};
      if (credForm.nome && credForm.nome !== credTarget.nome) payload.nome = credForm.nome;
      if (credForm.email && credForm.email !== credTarget.email) payload.email = credForm.email;
      if (credForm.senha) payload.senha = credForm.senha;
      if (isSelf) payload.current_password = credForm.current_password;
      if (Object.keys(payload).length === 0 || (isSelf && Object.keys(payload).length === 1)) {
        toast.info("Nada para atualizar");
        setCredSaving(false);
        return;
      }
      const { data } = await axios.put(`${API}/usuarios/${credTarget.id}/credenciais`, payload);
      if (isSelf) {
        // Atualiza localStorage user para refletir mudança de nome/email
        const newUser = { ...currentUser, nome: data.nome, email: data.email };
        localStorage.setItem("user", JSON.stringify(newUser));
      }
      toast.success(`Credenciais atualizadas${data.senha_alterada ? " (senha trocada)" : ""}!`);
      setCredDialogOpen(false);
      setCredTarget(null);
      fetchAcessos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao atualizar credenciais");
    } finally {
      setCredSaving(false);
    }
  };

  const admins = acessos.filter(a => a.tipo === 'admin');
  const vendedores = acessos.filter(a => a.tipo === 'vendedor');
  const secretarios = acessos.filter(a => a.tipo === 'secretario');

  // Permissões base (vendedor e secretário)
  const basePermissoes = [
    { key: "criar_cliente", label: "Criar Cliente" },
    { key: "editar_cliente", label: "Atualizar Cliente" },
    { key: "excluir_cliente", label: "Excluir Cliente" },
    { key: "criar_promissoria", label: "Criar Promissória" },
    { key: "editar_promissoria", label: "Editar Promissória" },
    { key: "quitar_promissoria", label: "Quitar Promissória" },
    { key: "excluir_promissoria", label: "Excluir Promissória" },
    { key: "compartilhar_pdf", label: "Compartilhar PDF" },
  ];
  // Permissões extras (somente secretário)
  const secretarioPermissoes = [
    { key: "acessar_dividas", label: "Acessar Dívidas" },
    { key: "acessar_fluxo_caixa", label: "Acessar Fluxo de Caixa" },
  ];

  if (loading) {
    return <div className="text-center py-8">Carregando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="acessos-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold">Acessos</h2>
          <p className="text-gray-500 mt-1">Gerencie vendedores, secretários e suas permissões</p>
        </div>
        <div className="flex gap-2">
          <Button className="gap-2 bg-blue-500 hover:bg-blue-600" onClick={() => openCreateDialog("vendedor")} data-testid="add-vendedor-button">
            <Truck className="h-4 w-4" />
            Novo Vendedor
          </Button>
          {temFeatureSync("secretario") ? (
            <Button className="gap-2 bg-purple-500 hover:bg-purple-600" onClick={() => openCreateDialog("secretario")} data-testid="add-secretario-button">
              <Briefcase className="h-4 w-4" />
              Novo Secretário
            </Button>
          ) : (
            <Button
              className="gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
              onClick={() => abrirUpgradeModal({
                feature_required: "secretario",
                plano_minimo: "pro",
                plano_minimo_nome: "Pro",
                message: "O acesso de Secretário é uma vantagem do plano Pro. Faça upgrade para liberar.",
              })}
              data-testid="add-secretario-upgrade-button"
            >
              <Lock className="h-4 w-4" />
              Novo Secretário (Pro)
            </Button>
          )}
        </div>
      </div>

      {/* Seção Administradores */}
      {admins.length > 0 && (
        <>
          <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
            <Shield className="h-5 w-5" /> Administradores ({admins.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {admins.map((item) => (
              <Card key={item.id} className="border-emerald-200" data-testid={`admin-card-${item.id}`}>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span>{item.nome}</span>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => abrirCredDialog(item)} title="Editar credenciais" data-testid={`cred-${item.id}`}>
                        <KeyRound className="h-4 w-4 text-emerald-600" />
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-sm text-gray-600">{item.email}</p>
                  <p className="text-sm text-emerald-600 font-medium">Conta principal</p>
                  <span className="inline-block text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Administrador</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Seção Vendedores */}
      {vendedores.length > 0 && (
        <>
          <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
            <Truck className="h-5 w-5" /> Vendedores ({vendedores.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vendedores.map((item) => (
              <Card key={item.id} data-testid={`vendedor-card-${item.id}`}>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span>{item.nome}</span>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => abrirCredDialog(item)} title="Editar credenciais" data-testid={`cred-${item.id}`}>
                        <KeyRound className="h-4 w-4 text-emerald-600" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openPermissoesDialog(item)} title="Permissões" data-testid={`perm-${item.id}`}>
                        <Shield className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openDeleteDialog(item)} title="Excluir" data-testid={`del-${item.id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-sm text-gray-600">{item.email}</p>
                  <p className="text-sm">
                    <span className="font-semibold">Carro:</span>{" "}
                    {carros.find(c => c.id === item.carro_id)?.nome || item.carro_id}
                  </p>
                  <span className="inline-block text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Vendedor</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Seção Secretários */}
      {secretarios.length > 0 && (
        <>
          <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2 mt-6">
            <Briefcase className="h-5 w-5" /> Secretários ({secretarios.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {secretarios.map((item) => (
              <Card key={item.id} className="border-purple-200" data-testid={`secretario-card-${item.id}`}>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span>{item.nome}</span>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => abrirCredDialog(item)} title="Editar credenciais" data-testid={`cred-${item.id}`}>
                        <KeyRound className="h-4 w-4 text-emerald-600" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openPermissoesDialog(item)} title="Permissões" data-testid={`perm-${item.id}`}>
                        <Shield className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openDeleteDialog(item)} title="Excluir" data-testid={`del-${item.id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-sm text-gray-600">{item.email}</p>
                  <p className="text-sm text-purple-600 font-medium">Acesso completo (escritório)</p>
                  <span className="inline-block text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Secretário</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {acessos.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            Nenhum acesso cadastrado ainda.
          </CardContent>
        </Card>
      )}

      {/* Dialog Criar Vendedor */}
      <Dialog open={dialogOpen && dialogType === "vendedor"} onOpenChange={(open) => { if (!open) setDialogOpen(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Vendedor</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitVendedor} className="space-y-4">
            <div>
              <Label>Nome Completo</Label>
              <Input value={formVendedor.nome} onChange={(e) => setFormVendedor({...formVendedor, nome: e.target.value})} required data-testid="vendedor-nome-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={formVendedor.email} onChange={(e) => setFormVendedor({...formVendedor, email: e.target.value})} required data-testid="vendedor-email-input" />
            </div>
            <div>
              <Label>Senha</Label>
              <PasswordInput value={formVendedor.senha} onChange={(e) => setFormVendedor({...formVendedor, senha: e.target.value})} required data-testid="vendedor-senha-input" />
            </div>
            <div>
              <Label>Carro</Label>
              <select value={formVendedor.carro_id} onChange={(e) => setFormVendedor({...formVendedor, carro_id: e.target.value})} required className="w-full px-3 py-2 border border-gray-300 rounded-md" data-testid="vendedor-carro-select">
                <option value="">Selecione um carro</option>
                {carros.map((carro) => (
                  <option key={carro.id} value={carro.id}>{carro.nome}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-blue-500 hover:bg-blue-600" data-testid="submit-vendedor">Criar Vendedor</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Criar Secretário */}
      <Dialog open={dialogOpen && dialogType === "secretario"} onOpenChange={(open) => { if (!open) setDialogOpen(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Secretário</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitSecretario} className="space-y-4">
            <div>
              <Label>Nome Completo</Label>
              <Input value={formSecretario.nome} onChange={(e) => setFormSecretario({...formSecretario, nome: e.target.value})} required data-testid="secretario-nome-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={formSecretario.email} onChange={(e) => setFormSecretario({...formSecretario, email: e.target.value})} required data-testid="secretario-email-input" />
            </div>
            <div>
              <Label>Senha</Label>
              <PasswordInput value={formSecretario.senha} onChange={(e) => setFormSecretario({...formSecretario, senha: e.target.value})} required data-testid="secretario-senha-input" />
            </div>
            <p className="text-sm text-purple-600 bg-purple-50 p-3 rounded-md">
              O secretário terá a mesma visualização do administrador. As permissões podem ser ajustadas após a criação.
            </p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-purple-500 hover:bg-purple-600" data-testid="submit-secretario">Criar Secretário</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Permissões */}
      <Dialog open={permissoesDialogOpen} onOpenChange={setPermissoesDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Permissões - {selectedItem?.nome}
              <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${selectedItem?.tipo === 'secretario' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                {selectedItem?.tipo === 'secretario' ? 'Secretário' : 'Vendedor'}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {basePermissoes.map(p => (
              <div key={p.key} className="flex items-center justify-between">
                <Label htmlFor={p.key}>{p.label}</Label>
                <Switch id={p.key} checked={permissoes[p.key] || false} onCheckedChange={(checked) => setPermissoes({...permissoes, [p.key]: checked})} data-testid={`perm-toggle-${p.key}`} />
              </div>
            ))}
            {selectedItem?.tipo === 'secretario' && (
              <>
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-semibold text-purple-700 mb-3">Acessos do Escritório</p>
                </div>
                {secretarioPermissoes.map(p => (
                  <div key={p.key} className="flex items-center justify-between">
                    <Label htmlFor={p.key}>{p.label}</Label>
                    <Switch id={p.key} checked={permissoes[p.key] || false} onCheckedChange={(checked) => setPermissoes({...permissoes, [p.key]: checked})} data-testid={`perm-toggle-${p.key}`} />
                  </div>
                ))}
              </>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setPermissoesDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleUpdatePermissoes} data-testid="save-permissoes">Salvar Permissões</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Editar Credenciais */}
      <Dialog open={credDialogOpen} onOpenChange={(open) => { setCredDialogOpen(open); if (!open) setCredTarget(null); }}>
        <DialogContent data-testid="dialog-cred">
          <DialogHeader>
            <DialogTitle>
              Editar credenciais — {credTarget?.nome}
              <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${credTarget?.tipo === 'admin' ? 'bg-emerald-100 text-emerald-700' : credTarget?.tipo === 'secretario' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                {credTarget?.tipo === 'admin' ? 'Admin' : credTarget?.tipo === 'secretario' ? 'Secretário' : 'Vendedor'}
              </span>
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={salvarCredenciais} className="space-y-4">
            <div>
              <Label>Nome Completo</Label>
              <Input value={credForm.nome} onChange={(e) => setCredForm({ ...credForm, nome: e.target.value })} data-testid="cred-nome" />
            </div>
            <div>
              <Label>Email (login)</Label>
              <Input type="email" value={credForm.email} onChange={(e) => setCredForm({ ...credForm, email: e.target.value })} data-testid="cred-email" />
            </div>
            <div>
              <Label>Nova Senha (deixe em branco para manter)</Label>
              <PasswordInput placeholder="Deixe em branco para não alterar" value={credForm.senha} onChange={(e) => setCredForm({ ...credForm, senha: e.target.value })} autoComplete="new-password" data-testid="cred-senha" />
            </div>
            {credTarget?.id === currentUser.id && (
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-md">
                <Label className="text-amber-800">Sua senha atual (confirmação) *</Label>
                <PasswordInput placeholder="Sua senha atual" value={credForm.current_password} onChange={(e) => setCredForm({ ...credForm, current_password: e.target.value })} autoComplete="current-password" required data-testid="cred-current-password" />
                <p className="text-[11px] text-amber-700 mt-1">Para sua segurança, ao editar a própria conta confirme com sua senha atual.</p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setCredDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={credSaving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-salvar-cred">
                {credSaving ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir <strong>{itemToDelete?.nome}</strong>?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setItemToDelete(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
